// Authentication Service
// Handles login, registration, token management, and profile lifecycle

import config, {
    clearStoredAccessToken,
    getStoredAccessToken,
    setStoredAccessToken,
} from './config.js';

let api = null;
const getSessionStorage = () =>
    typeof globalThis !== 'undefined' && globalThis.sessionStorage
        ? globalThis.sessionStorage
        : null;
async function getApi() {
    if (!api) {
        const apiModule = await import('./api.js');
        api = apiModule.api;
    }
    return api;
}

export class AuthService {
    constructor() {
        this.accessTokenKey = config.tokens.access;
        this.refreshTokenKey = config.tokens.refresh;
        this.userKey = 'pubquiz_user';
        this.impersonationKey = 'pubquiz_impersonation_admin';
    }

    getAccessToken() {
        return getStoredAccessToken();
    }

    getRefreshToken() {
        return localStorage.getItem(this.refreshTokenKey);
    }

    isLoggedIn() {
        return Boolean(this.getUser() && this.getAccessToken());
    }

    getUser() {
        try {
            const userData = localStorage.getItem(this.userKey);
            return userData ? JSON.parse(userData) : null;
        } catch {
            return null;
        }
    }

    setUser(user) {
        if (!user) {
            localStorage.removeItem(this.userKey);
            return;
        }

        localStorage.setItem(this.userKey, JSON.stringify(user));
    }

    isSuperadmin() {
        const user = this.getUser();
        return user?.role === 'SUPERADMIN';
    }

    async login(email, password) {
        const apiClient = await getApi();
        const response = await apiClient.post(config.endpoints.login, { email, password });
        const payload = response.data || {};

        this.setTokens(payload.accessToken);
        this.setUser(payload.user);
        getSessionStorage()?.removeItem(this.impersonationKey);

        return payload.user;
    }

    async register(data) {
        const apiClient = await getApi();
        const response = await apiClient.post(config.endpoints.register, data);
        const payload = response.data || {};

        this.setTokens(payload.accessToken);
        this.setUser(payload.user);

        return payload;
    }

    async refreshSession() {
        try {
            const apiClient = await getApi();
            const response = await apiClient.post(config.endpoints.refresh, {});
            const payload = response.data || {};
            if (!payload.accessToken) {
                this.clearTokens();
                return false;
            }
            this.setTokens(payload.accessToken);

            if (payload.user) {
                this.setUser(payload.user);
            }

            return true;
        } catch {
            this.clearTokens();
            return false;
        }
    }

    async getProfile() {
        const apiClient = await getApi();
        const response = await apiClient.get(config.endpoints.profile);
        this.setUser(response.data);
        return response.data;
    }

    async updateProfile(data) {
        const apiClient = await getApi();
        const response = await apiClient.patch(config.endpoints.updateProfile, data);
        this.setUser(response.data);
        return response.data;
    }

    async changePassword(currentPassword, newPassword) {
        const apiClient = await getApi();
        return apiClient.post(config.endpoints.changePassword, { currentPassword, newPassword });
    }

    setTokens(accessToken = null) {
        setStoredAccessToken(accessToken);
        localStorage.removeItem(this.refreshTokenKey);
    }

    getImpersonationState() {
        try {
            const raw = getSessionStorage()?.getItem(this.impersonationKey);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    isImpersonating() {
        return Boolean(this.getImpersonationState());
    }

    async impersonateUser(userId) {
        const apiClient = await getApi();
        const response = await apiClient.post(config.endpoints.adminUserImpersonate(userId));
        const payload = response.data || {};
        const adminSnapshot = {
            accessToken: this.getAccessToken(),
            user: this.getUser(),
            impersonator: payload.impersonator || null,
        };

        getSessionStorage()?.setItem(this.impersonationKey, JSON.stringify(adminSnapshot));
        this.setTokens(payload.accessToken);
        this.setUser(payload.user);

        return payload.user;
    }

    restoreAdminSession() {
        const snapshot = this.getImpersonationState();
        if (!snapshot?.accessToken || !snapshot?.user) {
            return false;
        }

        this.setTokens(snapshot.accessToken);
        this.setUser(snapshot.user);
        getSessionStorage()?.removeItem(this.impersonationKey);
        return true;
    }

    clearTokens() {
        clearStoredAccessToken();
        localStorage.removeItem(this.refreshTokenKey);
        localStorage.removeItem(config.tokens.csrf);
        localStorage.removeItem(this.userKey);
        getSessionStorage()?.removeItem(this.impersonationKey);
    }

    logout(options = {}) {
        const {
            revokeServerSession = true,
            redirectTo = 'index.html',
        } = options;

        if (revokeServerSession) {
            void fetch(`${config.apiUrl}${config.endpoints.logout}`, {
                method: 'POST',
                credentials: 'include',
                keepalive: true,
            }).catch(() => undefined);
        }

        this.clearTokens();
        window.location.href = redirectTo;
    }
}

export const auth = new AuthService();
